import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Pencil } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { useSubscriptionTiersList } from "./use-subscription-tiers-list";
import { useCatalogProducts } from "./use-catalog-products";
import { TierFormDialog } from "./TierFormDialog";
import type { SubscriptionTierConfig } from "./types";

const TH_CLASS =
  "px-3 py-2 text-left font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground";
const TD_CLASS = "px-3 py-2 font-sans text-sm text-foreground";

type DialogState =
  | { readonly mode: "closed" }
  | { readonly mode: "create" }
  | { readonly mode: "edit"; readonly tier: SubscriptionTierConfig };

export function SubscriptionTiersPage() {
  const { t } = useTranslation();
  const tiersQuery = useSubscriptionTiersList();
  const productsQuery = useCatalogProducts();
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const close = () => setDialog({ mode: "closed" });
  const closeDialog = (next: boolean) => {
    if (!next) close();
  };

  const tiers = tiersQuery.data ?? [];
  const products = productsQuery.data ?? [];
  const productNameById = useMemo(
    () => new Map((productsQuery.data ?? []).map((p) => [p.id, p.name])),
    [productsQuery.data],
  );

  const resolveProduct = (id: string | null): string => {
    if (!id) return t("subscriptionTiers.unlinked");
    return productNameById.get(id) ?? id;
  };

  return (
    <div data-testid="subscription-tiers-screen" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="brand-eyebrow">{t("subscriptionTiers.eyebrow")}</p>
          <h1 className="text-3xl">{t("subscriptionTiers.title")}</h1>
          <p className="font-sans text-sm text-muted-foreground">
            {t("subscriptionTiers.subtitle")}
          </p>
        </div>
        <Button data-testid="create-tier-button" onClick={() => setDialog({ mode: "create" })}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t("subscriptionTiers.createButton")}
        </Button>
      </header>

      {tiersQuery.isLoading && (
        <div data-testid="subscription-tiers-loading" className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {tiersQuery.isError && (
        <div
          data-testid="subscription-tiers-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t("subscriptionTiers.error")}
          </p>
        </div>
      )}

      {!tiersQuery.isLoading && !tiersQuery.isError && tiers.length === 0 && (
        <div
          data-testid="subscription-tiers-empty"
          className="rounded-lg border border-border bg-card p-8 text-center"
        >
          <p className="font-sans text-sm font-medium text-foreground">
            {t("subscriptionTiers.empty.title")}
          </p>
          <p className="mt-1 font-sans text-sm text-muted-foreground">
            {t("subscriptionTiers.empty.body")}
          </p>
        </div>
      )}

      {!tiersQuery.isLoading && !tiersQuery.isError && tiers.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full border-collapse">
            <thead className="border-b border-border bg-muted/30">
              <tr>
                <th className={TH_CLASS}>{t("subscriptionTiers.table.key")}</th>
                <th className={TH_CLASS}>{t("subscriptionTiers.table.displayName")}</th>
                <th className={TH_CLASS}>{t("subscriptionTiers.table.weight")}</th>
                <th className={TH_CLASS}>{t("subscriptionTiers.table.dailyPassCap")}</th>
                <th className={TH_CLASS}>{t("subscriptionTiers.table.product")}</th>
                <th className={TH_CLASS}>{t("subscriptionTiers.table.status")}</th>
                <th className={TH_CLASS}>
                  <span className="sr-only">{t("subscriptionTiers.table.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier) => (
                <tr
                  key={tier.id}
                  data-testid={`tier-row-${tier.key}`}
                  className="border-b border-border last:border-0"
                >
                  <td className={`${TD_CLASS} font-mono text-xs`}>{tier.key}</td>
                  <td className={TD_CLASS}>{tier.displayName}</td>
                  <td className={TD_CLASS}>{tier.weight}</td>
                  <td className={TD_CLASS}>
                    {t("subscriptionTiers.passesPerDay", { count: tier.dailyPassCap })}
                  </td>
                  <td className={TD_CLASS}>
                    {tier.stripeProductId ? (
                      resolveProduct(tier.stripeProductId)
                    ) : (
                      <span data-testid={`tier-unlinked-${tier.key}`} className="text-amber-700">
                        {t("subscriptionTiers.unlinked")}
                      </span>
                    )}
                  </td>
                  <td className={TD_CLASS}>
                    <StatusBadge active={tier.active} />
                  </td>
                  <td className={`${TD_CLASS} text-right`}>
                    <Button
                      data-testid={`edit-tier-${tier.key}-button`}
                      variant="outline"
                      size="sm"
                      onClick={() => setDialog({ mode: "edit", tier })}
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                      {t("subscriptionTiers.editButton")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog.mode === "create" && (
        <TierFormDialog
          mode="create"
          tiers={tiers}
          products={products}
          open
          onOpenChange={closeDialog}
          onSuccess={close}
        />
      )}
      {dialog.mode === "edit" && (
        <TierFormDialog
          mode="edit"
          tier={dialog.tier}
          tiers={tiers}
          products={products}
          open
          onOpenChange={closeDialog}
          onSuccess={close}
        />
      )}
    </div>
  );
}

function StatusBadge({ active }: { readonly active: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      className={
        active
          ? "inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 font-sans text-xs font-medium text-emerald-800"
          : "inline-flex items-center rounded-full bg-muted px-2 py-0.5 font-sans text-xs font-medium text-muted-foreground"
      }
    >
      {active ? t("subscriptionTiers.status.active") : t("subscriptionTiers.status.inactive")}
    </span>
  );
}
